/**
 * AddCategoryModal Component
 * 
 * Modal for adding new categories.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Category form integration
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see specs/003-categorisation-feature/spec.md
 */

import React, { useEffect, useRef } from 'react';
import { CategoryForm } from './CategoryForm';
import { CreateCategoryInput, UpdateCategoryInput, CategoryType, Category } from '../../../../types/category.types';
import { X } from 'lucide-react';

interface AddCategoryModalProps {
  onClose: () => void;
  onSubmit: (data: CreateCategoryInput) => void;
  categories: Category[];
  initialType?: CategoryType;
  loading?: boolean;
}

export function AddCategoryModal({ 
  onClose, 
  onSubmit, 
  categories,
  initialType,
  loading = false
}: AddCategoryModalProps): React.ReactElement {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // Focus trap and keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-category-title"
      aria-describedby="add-category-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <h2 id="add-category-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Add New Category
              </h2>
              <p id="add-category-description" className="text-sm text-gray-600 dark:text-gray-400">
                Create a new category to organize your transactions
              </p>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <CategoryForm
              categories={categories}
              onSubmit={onSubmit as (data: CreateCategoryInput | UpdateCategoryInput) => void}
              onCancel={onClose}
              loading={loading}
              initialType={initialType}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

